import React, { useMemo } from 'react';
import { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { AppLogo } from 'src/components/generic-components/AppLogo';
import { RecentEntityType, RecentsConstants } from 'src/services/recents/RecentsConstants';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import featuredBaseLayerDark from 'src/styles/landing-page/featured-base-layer-dark.svg';
import featuredBaseLayerLight from 'src/styles/landing-page/featured-base-layer-light.svg';
import ActionCard from './ActionCard';
import { SECTION_TITLES } from './landing-page-constants';

/** Preview card with base layer SVG and app logo overlaid in the top-left corner. */
const FeaturedAppPreview: React.FC<{ app: ApplicationFlatTableEntity }> = ({ app }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className="landing-featured-preview">
      <img src={isDarkMode ? featuredBaseLayerDark : featuredBaseLayerLight} alt="" className="landing-featured-preview__base" />
      {app.logoConfig && (
        <div className="landing-featured-preview__logo">
          <AppLogo logoConfig={app.logoConfig} size="medium" />
        </div>
      )}
    </div>
  );
};

/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Featured Apps section — horizontal row of flagship app preview cards.
 *
 * Shows only active flagship products (apps marked `isFlagship`).
 */
const RecentsFeaturedSection: React.FC = () => {
  const appContext = useAppContext();

  const displayedApps = useMemo<ApplicationFlatTableEntity[]>(() => {
    const priorityOrder = ['Prophecy', 'xPT', 'Verity', 'Odyssey', 'Cerberus'];
    const flagshipApps = appContext.parsedApplications.filter((app) => app.isActive && app.isFlagship);
    const sorted = [...flagshipApps].sort((a, b) => {
      const aIdx = priorityOrder.indexOf(a.applicationName);
      const bIdx = priorityOrder.indexOf(b.applicationName);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
    return sorted.slice(0, RecentsConstants.CAP_BY_TYPE[RecentEntityType.APP]);
  }, [appContext.parsedApplications]);

  return (
    <div className="landing-section landing-section--featured">
      <div className="landing-section__header">
        <h4>{SECTION_TITLES.recentsFeatured}</h4>
      </div>
      <div className="landing-section__cards-row">
        {displayedApps.map((app) => (
          <ActionCard
            key={app.id}
            title={app.applicationName}
            description={app.shortApplicationDescription}
            preview={<FeaturedAppPreview app={app} />}
            appId={app.id}
            embeddingEnabled={app.embeddingEnabled}
            applicationLink={app.applicationLink}
            urlName={app.urlName}
          />
        ))}
      </div>
    </div>
  );
};

export default RecentsFeaturedSection;
